import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, FlaskConical, GitBranch, RefreshCw, AlertCircle, CheckCircle2, XCircle, BadgeCheck, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const hdr = () => { const t = localStorage.getItem('token'); return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) }; };
const api = {
  patches: () => fetch(`${API_URL}/patches`, { headers: hdr() }).then(r => r.json()),
  patch: (id) => fetch(`${API_URL}/patches/${id}`, { headers: hdr() }).then(r => r.json()),
  rings: () => fetch(`${API_URL}/patches/rings`, { headers: hdr() }).then(r => r.json()),
  assignments: () => fetch(`${API_URL}/patches/assignments`, { headers: hdr() }).then(r => r.json()),
  baseline: () => fetch(`${API_URL}/patches/baseline`, { headers: hdr() }).then(r => r.json()),
  machineCheck: (id) => fetch(`${API_URL}/patches/machine/${id}/pending`, { headers: hdr() }).then(r => r.json()),
  suggested: (ringId) => fetch(`${API_URL}/patches/rings/${ringId}/suggested-window`, { headers: hdr() }).then(r => r.json()),
  post: (path, body) => fetch(`${API_URL}/patches${path}`, { method: 'POST', headers: hdr(), body: JSON.stringify(body || {}) }),
  put: (path, body) => fetch(`${API_URL}/patches${path}`, { method: 'PUT', headers: hdr(), body: JSON.stringify(body || {}) }),
};

const DOW = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']; // índice = day (0=Lun) del maintenance_window
const mwLabel = (mw) => {
  if (!mw || !mw.start) return 'sin ventana (despliegue inmediato)';
  const days = Array.isArray(mw.days) && mw.days.length ? mw.days.map(d => DOW[d]).join(' ') : 'todos';
  return `${days} · ${mw.start}–${mw.end}`;
};

const SEV = { critical: { c: 'var(--red)', l: 'Crítico' }, high: { c: 'var(--amber)', l: 'Alto' }, medium: { c: 'var(--amber)', l: 'Medio' }, low: { c: 'var(--amber)', l: 'Bajo' } };
const RING_LABEL = {
  locked: { t: 'Bloqueado', c: 'var(--ink-3)' }, pending: { t: 'Pendiente', c: 'var(--ink-2)' }, approved: { t: 'Aprobado', c: 'var(--accent)' },
  deploying: { t: 'Desplegando', c: 'var(--ink-2)' }, deployed: { t: 'En reposo', c: 'var(--ink-2)' }, soaked: { t: 'Reposo OK', c: 'var(--green)' },
  held: { t: 'En espera', c: 'var(--red)' }, failed: { t: 'Falló', c: 'var(--red)' }, done: { t: 'OK', c: 'var(--green)' },
};
const CHG_STATUS = {
  draft: { t: 'Borrador', c: 'var(--ink-2)' }, pending_approval: { t: 'Pendiente de aprobación (CAB)', c: 'var(--amber)' },
  approved: { t: 'Aprobado', c: 'var(--accent)' }, implementing: { t: 'En implementación', c: 'var(--ink-2)' },
  review: { t: 'En revisión', c: 'var(--ink-2)' }, closed: { t: 'Cerrado', c: 'var(--green)' },
  rejected: { t: 'Rechazado', c: 'var(--red)' }, cancelled: { t: 'Cancelado', c: 'var(--ink-3)' },
};

export default function Parches() {
  const [tab, setTab] = useState('validacion');
  const [patches, setPatches] = useState([]);
  const [rings, setRings] = useState([]);
  const [sel, setSel] = useState(null);      // patch detail
  const [selId, setSelId] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadList = useCallback(() => {
    setLoading(true);
    Promise.all([api.patches(), api.rings()]).then(([p, r]) => {
      setPatches(Array.isArray(p) ? p : []); setRings(Array.isArray(r) ? r : []);
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  const loadDetail = useCallback((id) => { setSelId(id); api.patch(id).then(setSel); }, []);
  useEffect(() => { if (selId) loadDetail(selId); }, [selId, loadDetail]);

  const flash = (m, ok = true) => { setMsg({ m, ok }); setTimeout(() => setMsg(null), 3500); };
  const act = async (p, body, okMsg) => {
    const r = await api.post(p, body);
    const j = await r.json().catch(() => ({}));
    if (r.ok) { flash(okMsg); loadDetail(selId); loadList(); } else { flash(j.error || 'Error', false); }
  };

  const rescan = async () => { flash('Rescaneando…'); const r = await api.post('/rescan', {}); const j = await r.json(); flash(r.ok ? `Catálogo: ${j.missing} pendientes en ${j.assets} equipos` : (j.error || 'Error'), r.ok); loadList(); };

  const S = styles;
  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <h1 style={S.title}><ShieldAlert size={22} style={{ verticalAlign: -4 }} /> Gestión de Parches</h1>
          <div style={S.sub}>Validá en laboratorio y desplegá por anillos con reinicios programados y rollback.</div>
        </div>
        <button style={S.btnGhost} onClick={rescan}><RefreshCw size={14} style={{ verticalAlign: -2 }} /> Rescan</button>
      </div>

      <div style={S.tabs}>
        <button onClick={() => setTab('validacion')} style={{ ...S.tab, ...(tab === 'validacion' ? S.tabOn : {}) }}><FlaskConical size={15} style={{ verticalAlign: -3 }} /> Validación</button>
        <button onClick={() => setTab('anillos')} style={{ ...S.tab, ...(tab === 'anillos' ? S.tabOn : {}) }}><GitBranch size={15} style={{ verticalAlign: -3 }} /> Despliegue por Anillos</button>
        <button onClick={() => setTab('aprobados')} style={{ ...S.tab, ...(tab === 'aprobados' ? S.tabOn : {}) }}><BadgeCheck size={15} style={{ verticalAlign: -3 }} /> Aprobados</button>
      </div>

      {msg && <div style={{ ...S.flash, borderColor: msg.ok ? 'var(--green)' : 'var(--red)' }}>{msg.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {msg.m}</div>}

      {tab === 'aprobados' && <BaselinePanel rings={rings} />}

      {tab !== 'aprobados' && <div style={S.cols}>
        {/* Lista de parches */}
        <div style={S.listCol}>
          <div style={S.listHdr}>Parches ({patches.length})</div>
          {loading && <div style={S.dim}>Cargando…</div>}
          {!loading && !patches.length && <div style={S.dim}>Sin parches detectados. Probá "Rescan".</div>}
          {patches.map((p) => (
            <div key={p.id} onClick={() => setSelId(p.id)} style={{ ...S.pItem, ...(selId === p.id ? S.pItemOn : {}) }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>{p.kb_number}</span>
                <span style={{ ...S.sevChip, background: (SEV[p.severity] || SEV.low).c }}>{(SEV[p.severity] || SEV.low).l}</span>
              </div>
              <div style={S.pTitle}>{p.title || 'Sin título'}</div>
              <div style={S.pMeta}>{p.missing} pendientes · {p.installed} instalados · {p.affected} equipos</div>
            </div>
          ))}
        </div>

        {/* Detalle */}
        <div style={S.detailCol}>
          {!sel && <div style={S.dim}>Elegí un parche de la izquierda.</div>}
          {sel && tab === 'validacion' && <ValidacionPanel patch={sel} rings={rings} act={act} put={api.put} reload={() => loadDetail(selId)} flash={flash} />}
          {sel && tab === 'anillos' && <AnillosPanel patch={sel} rings={rings} act={act} />}
          {!sel && tab === 'anillos' && <RingsManager rings={rings} reload={loadList} flash={flash} />}
        </div>
      </div>}
    </div>
  );
}

function ValidacionPanel({ patch, rings, act, put, reload, flash }) {
  const S = styles;
  const lab = rings.find(r => r.is_lab);
  const v = patch.validation;
  const [snap, setSnap] = useState('');
  const [labAsset, setLabAsset] = useState('');
  const [assets, setAssets] = useState([]);
  const [checklist, setChecklist] = useState(v?.checklist || []);
  useEffect(() => { setChecklist(v?.checklist || []); }, [v]);
  useEffect(() => { api.assignments().then(a => setAssets(Array.isArray(a) ? a : [])).catch(() => setAssets([])); }, []);

  // Equipos del anillo de laboratorio primero (son los candidatos naturales para la VM).
  const labId = lab?.id;
  const options = [...assets].sort((a, b) =>
    (a.ring_id === labId ? -1 : 0) - (b.ring_id === labId ? -1 : 0) || a.hostname.localeCompare(b.hostname));

  const startVal = () => {
    if (!snap.trim()) return flash('Registrá el snapshot_ref primero.', false);
    if (!labAsset) return flash('Elegí la VM de laboratorio.', false);
    act(`/${patch.id}/validation`, { asset_id: labAsset, snapshot_ref: snap.trim() }, 'Validación iniciada');
  };
  const setItem = (i, ok) => setChecklist(checklist.map((c, j) => j === i ? { ...c, ok } : c));
  const saveChecklist = async (result) => {
    const r = await put(`/validation/${v.id}`, { checklist, result });
    const j = await r.json().catch(() => ({}));
    flash(r.ok ? `Validación: ${j.result}` : (j.error || 'Error'), r.ok); reload();
  };

  return (
    <div>
      <PatchHeader patch={patch} />
      <div style={S.card}>
        <div style={S.cardT}><FlaskConical size={15} /> Validación en laboratorio (Anillo 0)</div>
        {!lab && <div style={S.warn}>No hay anillo de laboratorio (is_lab). Creá uno en la pestaña de Anillos.</div>}
        {!v && (
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            <div style={S.note}>Tomá un snapshot de la VM antes de instalar. Registrá su identificador:</div>
            <input placeholder="snapshot_ref (ej. lab-vm-2026-08-12-pre)" value={snap} onChange={e => setSnap(e.target.value)} style={S.input} />
            <select value={labAsset} onChange={e => setLabAsset(e.target.value)} style={S.input}>
              <option value="">— Elegí la VM de laboratorio —</option>
              {options.map(a => (
                <option key={a.asset_id} value={a.asset_id}>{a.hostname}{a.ring_id === labId ? ' (lab)' : ''}</option>
              ))}
            </select>
            {!assets.length && <div style={S.warn}>No hay equipos Windows para validar. Agregá equipos al inventario / anillo de laboratorio.</div>}
            <button style={S.btn} onClick={startVal}>Iniciar validación</button>
          </div>
        )}
        {v && (
          <div style={{ marginTop: 8 }}>
            <div style={S.pMeta}>Snapshot: <b>{v.snapshot_ref || '—'}</b> · Resultado: <b style={{ color: v.result === 'pass' ? 'var(--green)' : v.result === 'fail' ? 'var(--red)' : 'var(--ink-2)' }}>{v.result}</b></div>
            <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
              {checklist.map((c, i) => (
                <div key={i} style={S.chkRow}>
                  <span style={{ fontSize: 13 }}>{c.item} {c.blocking && <span style={S.blockTag}>bloqueante</span>}</span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setItem(i, true)} style={{ ...S.chkBtn, ...(c.ok === true ? { background: 'var(--green)', color: 'var(--surface)', borderColor: 'var(--green)' } : {}) }}><CheckCircle2 size={13} /></button>
                    <button onClick={() => setItem(i, false)} style={{ ...S.chkBtn, ...(c.ok === false ? { background: 'var(--red)', color: 'var(--surface)', borderColor: 'var(--red)' } : {}) }}><XCircle size={13} /></button>
                  </span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button style={S.btn} onClick={() => saveChecklist('pass')}>Marcar PASS (habilita Anillo 1)</button>
              <button style={{ ...S.btn, background: 'var(--red)' }} onClick={() => saveChecklist('fail')}>Marcar FAIL (held + rollback)</button>
            </div>
            {checklist.some(c => c.blocking && c.ok === false) && <div style={S.warn}>Un ítem bloqueante falló → el parche quedará <b>held</b> en todos los anillos.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function AnillosPanel({ patch, rings, act }) {
  const S = styles;
  const ringById = Object.fromEntries(rings.map(r => [r.id, r]));
  const confirmAct = (label, fn) => { if (window.confirm(label)) fn(); };
  const [sched, setSched] = useState({}); // { [ringId]: 'YYYY-MM-DDTHH:MM' }
  const deploy = (patchId, ringId, ringName) => {
    const when = sched[ringId];
    const iso = when ? new Date(when).toISOString() : null;
    const msg = iso
      ? `¿Programar ${patch.kb_number} en "${ringName}" para ${new Date(when).toLocaleString()}? Se dispara dentro de la ventana de mantenimiento.`
      : `¿Desplegar ${patch.kb_number} al anillo "${ringName}" ahora? Reinicia según la ventana.`;
    confirmAct(msg, () => act(`/${patchId}/deploy`, { ring_id: ringId, scheduled_for: iso }, iso ? 'Despliegue programado' : 'Despliegue creado'));
  };
  const chg = patch.change;
  const chgSt = chg ? (CHG_STATUS[chg.status] || CHG_STATUS.draft) : null;
  return (
    <div>
      <PatchHeader patch={patch} />
      {chg && (
        <div style={{ ...S.changeBanner, borderColor: chgSt.c }}>
          <span><GitBranch size={13} style={{ verticalAlign: -2 }} /> Cambio vinculado <a href={`/changes/${chg.id}`} style={S.chgLink}>{chg.change_number}</a> <span style={{ color: 'var(--ink-3)' }}>({chg.change_type})</span></span>
          <span style={{ fontWeight: 700, color: chgSt.c }}>{chgSt.t}</span>
        </div>
      )}
      {chg && chg.status === 'pending_approval' && <div style={S.warn}>El despliegue a producción está frenado hasta que el CAB apruebe el cambio {chg.change_number} en Gestión de Cambios.</div>}
      <div style={S.card}>
        <div style={S.cardT}><GitBranch size={15} /> Pipeline de despliegue</div>
        <div style={S.pipeline}>
          {(patch.pipeline || []).map((node, idx) => {
            const r = ringById[node.ring_id]; const st = RING_LABEL[node.state] || RING_LABEL.pending;
            return (
              <React.Fragment key={node.ring_id}>
                {idx > 0 && <div style={S.arrow}>→</div>}
                <div style={{ ...S.ring, borderColor: st.c }}>
                  <div style={{ fontWeight: 600, fontSize: 12.5 }}>{r?.name || 'Anillo'}</div>
                  <div style={{ ...S.ringState, color: st.c }}>{st.t}</div>
                  {node.locked && <div style={S.lockReason}>{node.reason}</div>}
                  {!node.is_lab && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                      <button disabled={node.locked} style={{ ...S.miniBtn, opacity: node.locked ? .4 : 1 }}
                        onClick={() => act(`/${patch.id}/approve`, { ring_id: node.ring_id, decision: 'approved' }, 'Anillo aprobado')}>Aprobar</button>
                      <input type="datetime-local" disabled={node.locked} value={sched[node.ring_id] || ''}
                        onChange={e => setSched(s => ({ ...s, [node.ring_id]: e.target.value }))}
                        title="Dejar vacío = ahora. Con fecha = se dispara en la ventana de mantenimiento."
                        style={{ ...S.miniInput, opacity: node.locked ? .4 : 1 }} />
                      <div style={S.mwHint}>Ventana: {mwLabel(r?.maintenance_window)}</div>
                      <button disabled={node.locked} style={{ ...S.miniBtn, opacity: node.locked ? .4 : 1 }}
                        onClick={() => deploy(patch.id, node.ring_id, r?.name)}>{sched[node.ring_id] ? 'Programar' : 'Desplegar'}</button>
                      <button style={{ ...S.miniBtn, color: 'var(--red)', borderColor: 'var(--red-dim)' }}
                        onClick={() => confirmAct(`¿Rollback de ${patch.kb_number} en "${r?.name}"?`, () => act(`/${patch.id}/rollback`, { ring_id: node.ring_id }, 'Rollback creado'))}>Rollback</button>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
          {!(patch.pipeline || []).length && <div style={S.dim}>No hay anillos definidos.</div>}
        </div>
      </div>
      <div style={S.card}>
        <div style={S.cardT}>Estado por equipo</div>
        <div style={{ display: 'grid', gap: 4, marginTop: 6 }}>
          {(patch.per_asset || []).map(a => (
            <div key={a.asset_id} style={S.assetRow}>
              <span style={{ fontSize: 13 }}>{a.hostname}</span>
              <span style={{ ...S.stateChip, background: stateColor(a.state) }}>{a.state}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RingsManager({ rings, reload, flash }) {
  const S = styles;
  const [name, setName] = useState(''); const [isLab, setIsLab] = useState(false); const [order, setOrder] = useState(rings.length);
  const create = async () => {
    if (!name.trim()) return flash('Nombre requerido', false);
    const r = await api.post('/rings', { name: name.trim(), order_index: Number(order), is_lab: isLab });
    if (r.ok) { flash('Anillo creado'); setName(''); reload(); } else flash('Error', false);
  };
  return (
    <div style={S.card}>
      <div style={S.cardT}><GitBranch size={15} /> Anillos</div>
      <div style={{ display: 'grid', gap: 8, margin: '8px 0' }}>
        {rings.map(r => (
          <div key={r.id} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px' }}>
            <span style={{ fontSize: 13 }}>{r.is_lab ? '🧪 ' : ''}{r.name} <span style={S.dim}>(orden {r.order_index}, {r.members} equipos, soak {r.min_soak_hours}h)</span></span>
            {!r.is_lab && <WindowEditor ring={r} reload={reload} flash={flash} />}
          </div>
        ))}
        {!rings.length && <div style={S.dim}>Sin anillos. Creá el Anillo 0 (lab), 1 (piloto), 2 (prod).</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <input placeholder="Nombre del anillo" value={name} onChange={e => setName(e.target.value)} style={S.input} />
        <input type="number" placeholder="orden" value={order} onChange={e => setOrder(e.target.value)} style={{ ...S.input, width: 70 }} />
        <label style={{ fontSize: 12.5, display: 'flex', gap: 4, alignItems: 'center' }}><input type="checkbox" checked={isLab} onChange={e => setIsLab(e.target.checked)} /> lab</label>
        <button style={S.btn} onClick={create}>Crear</button>
      </div>
      <AssignMembers rings={rings} flash={flash} />
    </div>
  );
}

function WindowEditor({ ring, reload, flash }) {
  const S = styles;
  const mw = ring.maintenance_window;
  const [on, setOn] = useState(!!(mw && mw.start));
  const [days, setDays] = useState(new Set(mw?.days || [0, 1, 2, 3, 4, 5, 6]));
  const [start, setStart] = useState(mw?.start || '02:00');
  const [end, setEnd] = useState(mw?.end || '05:00');
  const [busy, setBusy] = useState(false);

  const toggle = (d) => setDays(s => { const n = new Set(s); n.has(d) ? n.delete(d) : n.add(d); return n; });
  const save = async () => {
    setBusy(true);
    const body = { maintenance_window: on ? { days: [...days].sort((a, b) => a - b), start, end, tz_offset_min: -180 } : null };
    const r = await api.put(`/rings/${ring.id}`, body);
    setBusy(false);
    if (r.ok) { flash('Ventana guardada'); reload(); } else flash('Error guardando ventana', false);
  };
  const suggest = async () => {
    const j = await api.suggested(ring.id).catch(() => null);
    if (j && j.suggested) { setStart(`${String(j.suggested.from).padStart(2, '0')}:00`); setEnd(`${String(j.suggested.to).padStart(2, '0')}:00`); setOn(true); flash(`Sugerido: ${j.suggested.label}`); }
    else flash(j?.reason || 'Sin sugerencia', false);
  };

  return (
    <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
      <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={on} onChange={e => setOn(e.target.checked)} />
        Ventana de mantenimiento {on ? '' : '(desactivada → despliegue inmediato)'}
      </label>
      {on && (
        <>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {DOW.map((d, i) => (
              <button key={i} onClick={() => toggle(i)}
                style={{ ...S.dayChip, ...(days.has(i) ? { background: 'var(--ink)', color: 'var(--surface)', borderColor: 'var(--ink)' } : {}) }}>{d}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
            <input type="time" value={start} onChange={e => setStart(e.target.value)} style={{ ...S.input, width: 110 }} />
            <span>a</span>
            <input type="time" value={end} onChange={e => setEnd(e.target.value)} style={{ ...S.input, width: 110 }} />
            <span style={S.dim}>ART</span>
          </div>
        </>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button style={{ ...S.miniBtn, opacity: busy ? .5 : 1 }} disabled={busy} onClick={save}>Guardar ventana</button>
        <button style={S.miniBtn} onClick={suggest} title="Franja más ociosa según Disponibilidad">Sugerir</button>
      </div>
    </div>
  );
}

function AssignMembers({ rings, flash }) {
  const S = styles;
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const load = () => api.assignments().then(r => setRows(Array.isArray(r) ? r : []));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const setRing = async (assetId, currentRing, newRing) => {
    if (newRing) { await api.post(`/rings/${newRing}/members`, { add: [assetId] }); }
    else if (currentRing) { await api.post(`/rings/${currentRing}/members`, { remove: [assetId] }); }
    flash('Asignación actualizada'); load();
  };
  const filtered = rows.filter(r => !q || r.hostname.toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700 }}>Asignar equipos a anillos ({rows.length})</div>
        <input placeholder="Buscar equipo…" value={q} onChange={e => setQ(e.target.value)} style={{ ...S.input, maxWidth: 180 }} />
      </div>
      <div style={{ maxHeight: 300, overflowY: 'auto', display: 'grid', gap: 3 }}>
        {filtered.map(r => (
          <div key={r.asset_id} style={S.assetRow}>
            <span style={{ fontSize: 12.5 }}>{r.hostname}</span>
            <select value={r.ring_id || ''} onChange={e => setRing(r.asset_id, r.ring_id, e.target.value)} style={{ ...S.input, maxWidth: 200, padding: '4px 8px' }}>
              <option value="">— sin anillo —</option>
              {rings.map(rg => <option key={rg.id} value={rg.id}>{rg.name}</option>)}
            </select>
          </div>
        ))}
        {!filtered.length && <div style={S.dim}>Sin equipos.</div>}
      </div>
    </div>
  );
}

function BaselinePanel({ rings }) {
  const S = styles;
  const [base, setBase] = useState(null);
  const [assets, setAssets] = useState([]);
  const [machine, setMachine] = useState('');
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const ringName = Object.fromEntries((rings || []).map(r => [r.id, r.name]));

  useEffect(() => { api.baseline().then(b => setBase(Array.isArray(b) ? b : [])).catch(() => setBase([])); }, []);
  useEffect(() => { api.assignments().then(a => setAssets(Array.isArray(a) ? a : [])).catch(() => setAssets([])); }, []);

  const runCheck = (id) => {
    setMachine(id); setCheck(null);
    if (!id) return;
    setChecking(true);
    api.machineCheck(id).then(setCheck).catch(() => setCheck(null)).finally(() => setChecking(false));
  };
  const fmtWhen = (base || []).length;
  const dstr = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

  return (
    <div>
      {/* Chequeo de equipo nuevo */}
      <div style={S.card}>
        <div style={S.cardT}><BadgeCheck size={15} /> ¿Es seguro parchear este equipo?</div>
        <div style={S.note}>Cruzá los updates pendientes de un equipo contra el baseline validado. Los que ya pasaron por el lab son seguros de aplicar sin re-validar.</div>
        <select value={machine} onChange={e => runCheck(e.target.value)} style={{ ...S.input, marginTop: 8, maxWidth: 320 }}>
          <option value="">— Elegí un equipo Windows —</option>
          {[...assets].sort((a, b) => a.hostname.localeCompare(b.hostname)).map(a => (
            <option key={a.asset_id} value={a.asset_id}>{a.hostname}</option>
          ))}
        </select>
        {checking && <div style={S.dim}>Verificando…</div>}
        {check && !checking && (
          <div style={{ marginTop: 10 }}>
            {!check.pending.length && <div style={S.okBox}><CheckCircle2 size={14} /> {check.hostname || 'El equipo'} no tiene updates pendientes en el catálogo.</div>}
            {!!check.pending.length && (
              <>
                <div style={S.checkSummary}>
                  <b>{check.hostname}</b>: {check.summary.approved} de {check.summary.total} pendientes <b style={{ color: 'var(--green)' }}>ya validados</b> (seguros de aplicar) · {check.summary.total - check.summary.approved} requieren validación.
                </div>
                <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
                  {check.pending.map(u => (
                    <div key={u.id} style={S.baseRow}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {u.approved
                          ? <span style={{ ...S.pill, background: 'var(--green-dim)', color: 'var(--green)' }}><CheckCircle2 size={12} /> validado</span>
                          : <span style={{ ...S.pill, background: 'var(--amber-dim)', color: 'var(--amber)' }}><Clock size={12} /> requiere validación</span>}
                        <span style={{ fontWeight: 600, fontSize: 12.5 }}>{u.kb_number}</span>
                        <span style={S.baseTitle}>{u.title || ''}</span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {u.via_kb && <span style={S.viaTag}>vía {u.via_kb}</span>}
                        <span style={{ ...S.sevChip, background: (SEV[u.severity] || SEV.low).c }}>{(SEV[u.severity] || SEV.low).l}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Listado del baseline */}
      <div style={S.card}>
        <div style={S.cardT}><BadgeCheck size={15} /> Baseline validado {base ? `(${fmtWhen})` : ''}</div>
        <div style={S.note}>KBs que pasaron validación en laboratorio o quedaron aprobados en un anillo. Conocidos-buenos.</div>
        {!base && <div style={S.dim}>Cargando…</div>}
        {base && !base.length && <div style={S.dim}>Todavía no hay parches validados/aprobados. Validá uno en la pestaña Validación.</div>}
        {base && !!base.length && (
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={S.table}>
              <thead>
                <tr>
                  {['KB', 'Título', 'Producto', 'Sev.', 'Validado', 'Aprobado en'].map(h => <th key={h} style={S.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {base.map(p => (
                  <tr key={p.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={S.td}>
                      <b>{p.kb_number}</b>
                      {p.superseded_by_kb && <div style={S.supTag}>reemplazado por {p.superseded_by_kb}</div>}
                    </td>
                    <td style={{ ...S.td, maxWidth: 280 }}><div style={S.tdTitle}>{p.title || '—'}</div></td>
                    <td style={S.td}>{p.product || '—'}</td>
                    <td style={S.td}><span style={{ ...S.sevChip, background: (SEV[p.severity] || SEV.low).c }}>{(SEV[p.severity] || SEV.low).l}</span></td>
                    <td style={S.td}>
                      {p.validated_at
                        ? <span title={p.validated_by || ''}>{dstr(p.validated_at)}{p.validated_by ? ` · ${p.validated_by}` : ''}</span>
                        : <span style={S.dim2}>—</span>}
                    </td>
                    <td style={S.td}>
                      {Array.isArray(p.approved_rings) && p.approved_rings.length
                        ? p.approved_rings.map((a, i) => <span key={i} style={S.ringTag}>{a.ring}</span>)
                        : <span style={S.dim2}>solo lab</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PatchHeader({ patch }) {
  const S = styles;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 700 }}>{patch.kb_number}</span>
        <span style={{ ...S.sevChip, background: (SEV[patch.severity] || SEV.low).c }}>{(SEV[patch.severity] || SEV.low).l}</span>
        {patch.requires_reboot && <span style={S.rebootTag}>requiere reinicio</span>}
      </div>
      <div style={S.pMeta}>{patch.title}</div>
    </div>
  );
}

function stateColor(s) {
  return ({ missing: 'var(--ink-3)', installing: 'var(--ink-2)', installed: 'var(--green)', failed: 'var(--red)', rolled_back: 'var(--amber)', pending_reboot: 'var(--ink-2)' })[s] || 'var(--ink-3)';
}

const styles = {
  wrap: { padding: '22px 24px', maxWidth: 1120 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 14 },
  title: { fontSize: 20, fontWeight: 700, margin: 0 }, sub: { fontSize: 12.5, color: 'var(--ink-2)', marginTop: 3 },
  tabs: { display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden', marginBottom: 14 },
  tab: { font: 'inherit', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', background: 'var(--bg-subtle)', border: 0, padding: '8px 15px', cursor: 'pointer' },
  tabOn: { background: 'var(--ink)', color: 'var(--surface)' },
  flash: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 12px', border: '1px solid', borderRadius: 8, marginBottom: 12, background: 'var(--surface)' },
  cols: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16, alignItems: 'start' },
  listCol: { border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' },
  listHdr: { fontSize: 12, fontWeight: 700, color: 'var(--ink-2)', padding: '10px 12px', background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' },
  pItem: { padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' },
  pItemOn: { background: 'var(--accent-dim)' },
  pTitle: { fontSize: 12, color: 'var(--ink-2)', margin: '3px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pMeta: { fontSize: 11.5, color: 'var(--ink-2)' },
  sevChip: { fontSize: 10.5, fontWeight: 700, color: 'var(--surface)', padding: '2px 7px', borderRadius: 6 },
  detailCol: { minHeight: 200 },
  card: { border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 14 },
  cardT: { fontSize: 13.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 },
  dim: { fontSize: 12.5, color: 'var(--ink-3)', padding: 12 },
  note: { fontSize: 11.5, color: 'var(--ink-2)' },
  warn: { fontSize: 12, color: 'var(--amber)', background: 'var(--amber-dim)', border: '1px solid #fde68a', borderRadius: 8, padding: '7px 10px', marginTop: 8 },
  input: { fontSize: 13, padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, flex: 1, minWidth: 140 },
  btn: { font: 'inherit', fontSize: 13, fontWeight: 600, background: 'var(--ink)', color: 'var(--surface)', border: 0, borderRadius: 8, padding: '8px 14px', cursor: 'pointer' },
  btnGhost: { font: 'inherit', fontSize: 12.5, fontWeight: 600, background: 'var(--bg-subtle)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 12px', cursor: 'pointer' },
  chkRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' },
  chkBtn: { border: '1px solid var(--border)', background: 'var(--surface)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  blockTag: { fontSize: 10, fontWeight: 700, color: 'var(--red)', marginLeft: 6 },
  pipeline: { display: 'flex', alignItems: 'stretch', gap: 6, marginTop: 10, flexWrap: 'wrap' },
  ring: { border: '1.5px solid', borderRadius: 10, padding: '10px 12px', minWidth: 150, background: 'var(--surface)' },
  ringState: { fontSize: 12, fontWeight: 700, marginTop: 2 },
  lockReason: { fontSize: 10.5, color: 'var(--ink-2)', marginTop: 4 },
  arrow: { alignSelf: 'center', color: 'var(--ink-3)', fontSize: 18 },
  miniBtn: { font: 'inherit', fontSize: 11.5, fontWeight: 600, background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' },
  miniInput: { font: 'inherit', fontSize: 11, border: '1px solid var(--border)', borderRadius: 6, padding: '3px 5px' },
  mwHint: { fontSize: 10.5, color: 'var(--ink-2)', lineHeight: 1.3 },
  dayChip: { font: 'inherit', fontSize: 11, fontWeight: 600, background: 'var(--surface)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 7px', cursor: 'pointer' },
  assetRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', background: 'var(--bg-subtle)', borderRadius: 6 },
  stateChip: { fontSize: 10.5, fontWeight: 700, color: 'var(--surface)', padding: '2px 7px', borderRadius: 6 },
  rebootTag: { fontSize: 10.5, fontWeight: 600, color: 'var(--ink-2)', border: '1px solid #a5f3fc', borderRadius: 5, padding: '1px 6px' },
  okBox: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--green)', background: 'var(--green-dim)', border: '1px solid var(--green-dim)', borderRadius: 8, padding: '8px 10px' },
  checkSummary: { fontSize: 12.5, color: 'var(--ink-2)', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' },
  baseRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '6px 8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 },
  baseTitle: { fontSize: 11.5, color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pill: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 20, flexShrink: 0 },
  viaTag: { fontSize: 10.5, fontWeight: 600, color: '#3730a3',},
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 },
  th: { textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--ink-2)', padding: '6px 8px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '7px 8px', verticalAlign: 'top', color: 'var(--ink-2)' },
  tdTitle: { maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  dim2: { color: 'var(--ink-3)' },
  supTag: { fontSize: 10, color: 'var(--amber)', marginTop: 2 },
  ringTag: { fontSize: 10.5, fontWeight: 600, color: 'var(--ink)', marginRight: 4, display: 'inline-block' },
  changeBanner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 12.5, padding: '8px 12px', border: '1px solid', borderLeftWidth: 3, borderRadius: 8, marginBottom: 12, background: 'var(--surface)' },
  chgLink: { fontWeight: 700, color: 'var(--accent)', textDecoration: 'none' },
};
